import React, { Component } from 'react';
import PropTypes from 'prop-types';
import sha256 from 'crypto-js/sha256';
import sha1 from 'crypto-js/sha1';
import getConfig from 'next/config';
import { connect } from 'react-redux';
import get from 'lodash/get';
import find from 'lodash/find';
import Case from 'case';

import experimentationId from './helpers/sync-with-experimentation-id.ts';
import { ReduxContext, storageAvailable } from './helpers';
import { getCookie } from './helpers/getCookie.ts';
import { getSearchParam } from './helpers/getSearchParam.ts';
import datadogRumClient from './utils/datadog-rum';
import { planPrices } from './utils/prices.ts';

const { publicRuntimeConfig } = getConfig();

function syncWithExperimentationId(userId, userInfo) {
  experimentationId.sync(
    window.analytics && typeof window.analytics.user === 'function'
      ? window.analytics.user().anonymousId()
      : sessionStorage.getItem('anonymousId'),
    userId,
    userInfo
  );
}

function trackEvent(
  name,
  props,
  firstName,
  lastName,
  phoneNumber,
  email,
  zip,
  isFbPixelEnabled,
  delaySegmentTrackEvent
) {
  if (window.analytics) {
    let timezone = null;
    if (
      window.Intl &&
      typeof Intl.DateTimeFormat().resolvedOptions().timeZone === 'string'
    ) {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    const context = {
      traits: {
        firstName,
        lastName,
        phone: phoneNumber,
        email
      },
      timezone
    };
    if (zip) context.traits.address = { postalCode: zip };
    if (storageAvailable('localStorage')) {
      const impactClickID = localStorage.getItem('irclickid');
      if (impactClickID) {
        context.referrer = {
          type: 'impactRadius',
          id: impactClickID
        };
      }
    }

    if (delaySegmentTrackEvent) {
      setTimeout(() => window.analytics.track(name, props, context), 1000);
      setTimeout(() => datadogRumClient.addAction(name, props), 1000);
    } else {
      window.analytics.track(name, props, context);
    }
  }
  logAdOptimizationEvent(name, isFbPixelEnabled, props);
}

const logAdOptimizationEvent = (name, isFbPixelEnabled, payload = {}) => {
  const fbStdPropsByName = {
    'Add Debit Card Clicked': 'AddPaymentInfo',
    'Add Debit Card Failed': null,
    'Add Instant Bank Clicked': 'AddPaymentInfo',
    'Add Manual ACH Clicked': 'AddPaymentInfo',
    'Confirm Code Viewed': 'Lead',
    'Confirmation Code Entered': null,
    'Email Entered': null,
    'Funding Source Verified': null,
    'Invest Plan Selected': null,
    'Max Plan Selected': null,
    'Infinity Plan Selected': null,
    'Parent Registration Complete': 'CompleteRegistration',
    'Registration Add Child Viewed': null,
    'Registration Add Funding Account Viewed': 'InitiateCheckout',
    'Registration Choose Role Viewed': null,
    'Registration Complete Viewed': null,
    'Registration DOB Viewed': null,
    'Registration Enter Email Viewed': null,
    'Registration Enter Password Viewed': null,
    'Registration Initial Funding Viewed': null,
    'Registration Invite Parent Viewed': null,
    'Role Selected': null,
    'Save Debit Viewed': null,
    'Verify Debit Card Clicked': null,
    'Verify Debit Card Failed': null,
    'Verify Debit Card Skipped': null,
    'Verify Debit Viewed': null
  };

  if (Object.prototype.hasOwnProperty.call(fbStdPropsByName, name)) {
    payload.Source = 'Web';
    if (window.fbq && isFbPixelEnabled) {
      if (payload.$email) payload.$email = _Analytics.fbHash(payload.$email);
      window.fbq('trackCustom', name, { ...payload, isFromPixel: 'true' });
      const fbStdProp = fbStdPropsByName[name];
      if (fbStdProp) {
        window.fbq('track', fbStdProp, { ...payload, isFromPixel: 'true' });
      }
    }
  }
};

class _Analytics {
  constructor({
    isFbPixelEnabled,
    firstName,
    lastName,
    phoneNumber,
    email,
    zip,
    referralCode,
    promotionId,
    planValue
  }) {
    this.isFbPixelEnabled = isFbPixelEnabled;
    this.firstName = firstName;
    this.lastName = lastName;
    this.phoneNumber = phoneNumber;
    this.email = email;
    this.zip = zip;
    this.referralCode = referralCode;
    this.promotionId = promotionId;
    this.planValue = planValue;
  }

  static getUserId(user) {
    return `${publicRuntimeConfig.analyticsPrefix}${user.id}`;
  }

  identifyUser({ flowId }) {
    if (window.analytics) {
      if (storageAvailable('localStorage')) {
        const lastUsedUserId = localStorage
          ? localStorage.getItem('lastAnalyticsUserId')
          : null;
        const lastUsedUserEmail = localStorage
          ? localStorage.getItem('lastAnalyticsUserEmail')
          : null;
        const adsSuperProperties = JSON.parse(
          localStorage.getItem('adsSuperProperties')
        );
        if (lastUsedUserId && lastUsedUserEmail) {
          const userInfo = {
            userId: lastUsedUserId,
            email: lastUsedUserEmail,
            flowId
          };
          if (adsSuperProperties) {
            window.analytics.identify(lastUsedUserId, {
              ...userInfo,
              utm_source: adsSuperProperties.utm_source,
              utm_medium: adsSuperProperties.utm_medium,
              utm_campaign: adsSuperProperties.utm_campaign,
              utm_term: adsSuperProperties.utm_term,
              utm_content: adsSuperProperties.utm_content
            });
          } else {
            window.analytics.identify(lastUsedUserId, {
              ...userInfo
            });
          }
        }
        return true;
      }
    }
  }

  loginUser(user) {
    const userId = _Analytics.getUserId(user);
    const userInfo = {
      role: user.role
    };
    if (user.email) {
      userInfo.email = user.email;
    }
    if (user.phoneNumber) {
      userInfo.phone = user.phoneNumber;
    }
    if (storageAvailable('localStorage')) {
      const adsSuperProperties = JSON.parse(
        localStorage.getItem('adsSuperProperties')
      );

      userInfo.utm_source = adsSuperProperties.utm_source;
      userInfo.utm_medium = adsSuperProperties.utm_medium;
      userInfo.utm_campaign = adsSuperProperties.utm_campaign;
      userInfo.utm_term = adsSuperProperties.utm_term;
      userInfo.utm_content = adsSuperProperties.utm_content;
    }
    if (window.analytics) {
      window.analytics.identify(userId, userInfo);
    }
    syncWithExperimentationId(userId, userInfo);

    if (storageAvailable('localStorage')) {
      localStorage.setItem('lastAnalyticsUserId', userId);
      localStorage.setItem('lastAnalyticsUserEmail', user.email);
    }

    this.updateFBIdentity(user);
    if (window.dataLayer) {
      window.dataLayer.push(
        Object.assign({ event: 'User Logged In', uid: userId })
      );
    }
  }

  static fbHash(any) {
    return sha256(String.prototype.toLowerCase.apply(any).trim());
  }

  /**
   *
   * @param obj could be user or object with id and address params, must include id property so that we can map the props
   */
  static getFBUserProps(obj) {
    // GD-4778

    const props = {};
    if (obj && obj.id) {
      if (obj.id) {
        Object.assign(props, { uid: _Analytics.getUserId(obj) });
      }
      if (obj.email) {
        Object.assign(props, { $email: this.fbHash(obj.email) });
      }
      if (obj.phoneNumber) {
        Object.assign(props, { phone_number: obj.phoneNumber });
      }
      if (obj.firstName) {
        Object.assign(props, { first_name: this.fbHash(obj.firstName) });
      }
      if (obj.lastName) {
        Object.assign(props, { last_name: this.fbHash(obj.lastName) });
      }
      if (obj.gender) {
        Object.assign(props, { $gender: obj.gender });
      }
      if (obj.dob) {
        Object.assign(props, { date_of_birth: this.fbHash(obj.dob) });
      }
      if (obj.city) {
        Object.assign(props, { $city: obj.city });
      }
      if (obj.country) {
        Object.assign(props, { $country: 'USA' });
      }
      if (obj.state) {
        Object.assign(props, { $state: obj.state });
      }
      if (obj.zip) {
        Object.assign(props, { $zipcode: obj.zip });
      }
    }
    return props;
  }

  /**
   *
   * @param obj could be user or object with id and address params, must include id property so that we can map the props
   */
  static getGTMUserProps(obj) {
    // GD-4778
    const props = {};
    if (obj && obj.id) {
      if (obj.id) {
        Object.assign(props, { uid: _Analytics.getUserId(obj) });
      }
      if (obj.email) {
        Object.assign(props, { email: obj.email });
      }
      if (obj.phoneNumber) {
        Object.assign(props, { phone_number: obj.phoneNumber });
      }
      if (obj.firstName) {
        Object.assign(props, { first_name: obj.firstName });
      }
      if (obj.lastName) {
        Object.assign(props, { last_name: obj.lastName });
      }
      if (obj.gender) {
        Object.assign(props, { gender: obj.gender });
      }
      if (obj.dob) {
        Object.assign(props, { date_of_birth: obj.dob });
      }
      if (obj.city) {
        Object.assign(props, { city: obj.city });
      }
      if (obj.state) {
        Object.assign(props, { state: obj.state });
      }
      if (obj.zip) {
        Object.assign(props, { zipcode: obj.zip });
      }
    }
    return props;
  }

  updateFBIdentity(user) {
    if (window.dataLayer) {
      window.dataLayer.push(_Analytics.getGTMUserProps(user));
    }
  }

  registerUser(user, flowId, smsNotificationsOptedStatus) {
    const userId = _Analytics.getUserId(user);
    let adsSuperProperties = {};
    if (storageAvailable('localStorage')) {
      localStorage.setItem('lastAnalyticsUserId', userId);
      localStorage.setItem('lastAnalyticsUserEmail', user.email);
      adsSuperProperties =
        JSON.parse(localStorage.getItem('adsSuperProperties')) || {};
    }

    syncWithExperimentationId(userId, { email: user.email, role: user.role });

    // Login that user for the first time
    if (window.analytics && typeof window.analytics.user === 'function') {
      window.analytics.alias(userId);

      if (flowId) {
        setTimeout(() => {
          window.analytics.identify(userId, {
            userId,
            email: user.email,
            role: user.role,
            flowId,
            utm_source: adsSuperProperties.utm_source,
            utm_medium: adsSuperProperties.utm_medium,
            utm_campaign: adsSuperProperties.utm_campaign,
            utm_term: adsSuperProperties.utm_term,
            utm_content: adsSuperProperties.utm_content
          });
        }, 1000);
      } else {
        setTimeout(() => {
          window.analytics.identify(userId, {
            userId,
            email: user.email,
            role: user.role,
            utm_source: adsSuperProperties.utm_source,
            utm_medium: adsSuperProperties.utm_medium,
            utm_campaign: adsSuperProperties.utm_campaign,
            utm_term: adsSuperProperties.utm_term,
            utm_content: adsSuperProperties.utm_content
          });
        }, 1000);
      }

      this.updateFBIdentity(user);
      if (window.dataLayer) {
        window.dataLayer.push(
          Object.assign({ event: 'User Registered', uid: userId })
        );
      }
    }

    this.trackEvent('Account Created', {
      eventProps: {
        email: user.email,
        phone_number: user.phoneNumber,
        role: user.role,
        partner_user_id: _Analytics.getUserId(user),
        preregistering_child:
          user.children &&
          user.children.length &&
          _Analytics.getUserId(user.children[0])
      },
      delaySegmentTrackEvent: false
    });

    if (smsNotificationsOptedStatus) {
      // Event needs timeout so that Account Created is fired first
      setTimeout(
        () =>
          this.trackEvent('Registration SMS Opted In', {
            eventProps: {
              opted_in: smsNotificationsOptedStatus === 'opted_in'
            },
            delaySegmentTrackEvent: true
          }),
        3000
      );
    }
  }

  registrationComplete(user, price) {
    if (user.role === 'parent') {
      this.registrationCompleteFacebook({ user, price });
      this.updateFBIdentity(user);

      const delaySegmentTrackEvent = false;
      const eventProps = {
        coupon: this.promotionId || null,
        order_promo_code:
          this.referralCode === 'null' ? null : this.referralCode,
        value: this.planValue,
        userId: user.id
      };
      if (storageAvailable('localStorage'))
        eventProps.gclid = localStorage.getItem('gclid');

      this.trackEvent('Parent Registration Complete', {
        eventProps,
        delaySegmentTrackEvent
      });

      if (storageAvailable('localStorage')) {
        const impactClickID = localStorage.getItem('irclickid');
        if (impactClickID) {
          this.segmentTrackEvent(
            'Online Lead',
            {
              ClickId: impactClickID,
              CustomerId: user.id,
              CustomerEmail: sha1(user.email).toString(),
              CampaignId: 11976,
              ActionTrackerId: 22009,
              EventDate: 'NOW',
              OrderId: user.id,
              OrderPromoCode:
                this.referralCode === 'null' ? null : this.referralCode
            },
            delaySegmentTrackEvent
          );
        }
      }
    }
  }

  registrationCompleteFacebook({ user, price }) {
    if (user.role === 'parent') {
      const { _tfa: tfa } = window;
      if (tfa && typeof tfa.push === 'function') {
        tfa.push({
          notify: 'event',
          name: 'RegistrationComplete',
          id: 1264368
        });
      }

      // Off for some envs
      if (window.fbq && this.isFbPixelEnabled) {
        window.fbq('track', 'fb_mobile_complete_registration');
        window.fbq('track', 'Purchase', {
          value: `${price}`,
          currency: 'USD'
        });
      }
    }
  }

  trackBankAccountVerifiedEvent(type) {
    if (['checking', 'debit'].indexOf(type) !== -1) {
      if (window.fbq && this.isFbPixelEnabled) {
        // Off for some lower envs
        window.fbq('track', `verified-${type}-account`);
      }
    }
  }

  segmentTrackEvent(name, eventProps, delaySegmentTrackEvent = true) {
    trackEvent(
      name,
      eventProps,
      this.firstName,
      this.lastName,
      this.phoneNumber,
      this.email,
      this.zip,
      this.isFbPixelEnabled,
      delaySegmentTrackEvent
    );
  }

  /**
   * Track an event in segment, and optionally in datadog RUM.
   * @param {object} eventParams
   * @param {string} eventParams.name - Name of the event to track
   * @param {boolean} eventParams.trackInRum - Determines whether to track this event in datadog RUM
   * @param {object} eventParams.eventProps - Properties/attributes associated with each event
   * @param {boolean} eventParams.delayTrackEvent -
   */
  trackEvent(
    name,
    { trackInRum = true, eventProps, delaySegmentTrackEvent = true } = {}
  ) {
    this.segmentTrackEvent(name, eventProps, delaySegmentTrackEvent);

    if (trackInRum) {
      datadogRumClient.addAction(name, eventProps);

      if (
        name === 'Registration Error Triggered' &&
        get(eventProps, 'registration_step')
      ) {
        datadogRumClient.addAction(
          `${Case.capital(eventProps.registration_step)} Error - ${
            eventProps.error_message
          }`
        );
      }
    }
  }

  /**
   * Track an event in segment, and optionally in datadog RUM.
   * @param {object} eventParams
   * @param {string} eventParams.name - Name of the event to track
   * @param {object} eventParams.eventProps - Properties/attributes associated with each event
   */
  trackRUMEvent(name, { eventProps } = {}) {
    datadogRumClient.addAction(name, eventProps);
  }

  page(name, category, props) {
    if (window.analytics && typeof window.analytics.page === `function`) {
      window.analytics.page(name, category, props);
    }
  }

  logAdOptimizationEvent(name, payload = {}) {
    logAdOptimizationEvent(name, this.isFbPixelEnabled, payload);
  }

  getAnonymousId = () => {
    const anonymousIdFromUrl = getSearchParam('anonymous_id');
    let anonIdFromGlCookie;
    let anonIdFromSessionStorage;
    let anonIdFromLocalStorage;
    let anonIdFromSegmentCookie;
    try {
      anonIdFromGlCookie = getCookie('gl_anonymous_id');
    } catch (e) {
      /* ignore failed cookie lookup */
    }
    if (storageAvailable('sessionStorage')) {
      try {
        anonIdFromSessionStorage = sessionStorage.getItem('anonymousId');
      } catch (e) {
        /* ignore failed session storage lookup */
      }
    }
    if (storageAvailable('localStorage')) {
      try {
        anonIdFromLocalStorage = JSON.parse(
          localStorage.getItem('ajs_anonymous_id') || '""'
        );
      } catch (e) {
        /* ignore failed local storage lookup */
      }
    }
    try {
      anonIdFromSegmentCookie = getCookie('ajs_anonymous_id');
    } catch (e) {
      /* ignore failed cookie lookup */
    }
    return (
      anonymousIdFromUrl ||
      anonIdFromGlCookie ||
      anonIdFromSessionStorage ||
      anonIdFromLocalStorage ||
      anonIdFromSegmentCookie ||
      undefined
    );
  };

  // Since mixpanel is loaded in devicemode, anonymous ids diverge if used after account creation,
  // So add the segment anonymous id to mixpanel as an identity.
  aliasMixpanel = anonymousId => {
    if (window.mixpanel && typeof window.mixpanel.alias === `function`) {
      window.mixpanel.alias(anonymousId);
    } else {
      setTimeout(this.aliasMixpanel, 2000); // if mixpanel doesnt exist yet, try again in 2 seconds
    }
  };

  updateAnonymousId = (anonymousId, attempts = 0) => {
    if (attempts < 5) {
      const newAttempts = attempts + 1;
      if (window.analytics && typeof window.analytics.user === `function`) {
        window.analytics.user().anonymousId(anonymousId);
      } else {
        return setTimeout(
          () => this.updateAnonymousId(anonymousId, newAttempts),
          300
        );
      }

      this.aliasMixpanel(anonymousId);
    }
  };
}
// eslint-disable-next-line import/no-mutable-exports
let allAnalytics;

class AnalyticsProvider extends Component {
  getPlanValue() {
    const { allPlans, selectedPlan } = this.props;
    const plan = find(allPlans, i => i.name === selectedPlan);
    return (
      (plan && plan.fee) ||
      planPrices(get(this, 'props.app.featureToggles')).standard
    );
  }

  getChildContext() {
    allAnalytics = new _Analytics({
      isFbPixelEnabled: this.props.isFbPixelEnabled,
      firstName: this.props.firstName,
      lastName: this.props.lastName,
      phoneNumber: this.props.phoneNumber,
      email: this.props.email,
      zip: this.props.zip,
      referralCode: this.props.referralCode,
      promotionId: this.props.promotionId,
      planValue: this.getPlanValue()
    });
    return {
      allAnalytics,
      segment: allAnalytics,
      // adding this line feels redundant, but I wanted to add a trackEvent method
      // that was outside of the provider's segment namespace.
      analytics: allAnalytics
    };
  }

  render() {
    return <div>{this.props.children}</div>;
  }
}

AnalyticsProvider.childContextTypes = {
  allAnalytics: PropTypes.object,
  segment: PropTypes.object,
  analytics: PropTypes.object
};

const AnalyticsProviderWithRedux = connect(
  state => ({
    isFbPixelEnabled: state.app.isFbPixelEnabled,
    firstName: get(state, 'app.answers.setLegalFirstName'),
    lastName: get(state, 'app.answers.setLegalLastName'),
    phoneNumber: get(state, 'app.tempPhoneNumber', undefined),
    email: get(state, 'app.answers.setUserEmail'),
    zip: get(state, 'app.answers.setLegalAddress.zip'),
    referralCode: get(state, 'app.answers.setReferralCode'),
    promotionId: get(state, 'app.promotionId', undefined),
    selectedPlan: get(state, 'app.answers.setPlan.pricingPlanName', undefined),
    allPlans: get(state, 'app.allPricingPlans', undefined)
  }),
  null,
  null,
  { context: ReduxContext }
)(AnalyticsProvider);

export {
  AnalyticsProviderWithRedux as AnalyticsProvider,
  allAnalytics as Analytics
};
